import React, { useState, useRef, useEffect } from 'react';
import { Upload, FileText, Image, AlertCircle, BarChart2, Check } from 'lucide-react';
import { ExtractionPreview } from './ExtractionPreview';

export function UploadStep({ onUploadComplete }) {
  const [uploadType, setUploadType] = useState(null); // 'csv' | 'ocr' | 'demo' | 'manual'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dexterity, setDexterity] = useState('RH'); // RH (Right-Handed) or LH (Left-Handed)
  const [extractedData, setExtractedData] = useState(null); // For OCR preview
  const [pendingFileName, setPendingFileName] = useState(null); // Store filename for later use
  const loadingTimeoutRef = useRef(null);

  const transformToSessionData = (apiData, fileName = 'Uploaded Session') => {
    // Transform API response to session data structure
    const shots = apiData.shots || [];

    return {
      session: {
        id: `session-${Date.now()}`,
        name: fileName.replace('.csv', ''),
        date: new Date().toISOString().split('T')[0],
        shotGroups: [
          {
            id: 'group-1',
            name: fileName.replace('.csv', ''),
            color: '#ff6600',
            isBaseline: true,
            shots: shots.map((shot, idx) => ({
              shotNumber: idx + 1,
              included: true,
                metrics: {
                clubSpeed: shot.club_speed || 0,
                ballSpeed: shot.ball_speed || 0,
                smashFactor: shot.smash_factor || 0,
                launchAngle: shot.launch_angle || 0,
                spinRate: shot.spin_rate || 0,
                spinAxis: shot.spin_axis || 0,
                sideSpin: shot.side_spin || 0,
                carry: shot.carry_distance || 0,
                total: shot.total_distance || 0,
                landingAngle: shot.landing_angle || 0,
                height: shot.max_height || 0,
                attackAngle: shot.attack_angle || 0,
                offlineDistance: shot.offline_distance || 0,
                clubPath: shot.club_path || 0,
                faceAngle: shot.face_angle || 0,
                dynamicLoft: shot.dynamic_loft || 0,
              }
            }))
          }
        ]
      }
    };
  };

  const handleFileUpload = async (event, type) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setLoading(true);
    setError(null);

    // Safety timeout to prevent infinite loading (70 seconds)
    loadingTimeoutRef.current = setTimeout(() => {
      console.error('Upload exceeded 70-second safety timeout');
      setLoading(false);
      setError('Upload timed out. Please try again with a smaller file or check your connection.');
    }, 70000);

    try {
      const formData = new FormData();

      if (type === 'csv') {
        formData.append('file', file);
        formData.append('category', 'Driver');
        formData.append('dexterity', dexterity);
        formData.append('club_type', 'Driver');
        formData.append('playing_conditions', 'false');

        const response = await fetch('/api/analyze/upload', {
          method: 'POST',
          body: formData,
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          const errorMsg = typeof errorData.detail === 'string'
            ? errorData.detail
            : typeof errorData.detail === 'object'
            ? JSON.stringify(errorData.detail)
            : errorData.message || 'Upload failed';
          throw new Error(errorMsg);
        }

        const data = await response.json();

        // DEBUG: Log what backend returned
        console.log('=== CSV UPLOAD RESPONSE ===');
        console.log('Full response:', data);
        console.log('Has validation:', !!data.validation);
        console.log('Validation array length:', data.validation?.length);
        console.log('First shot:', data.shots?.[0]);
        console.log('===========================');

        // Show extraction preview for user verification (same as OCR)
        setExtractedData(data);
        setPendingFileName(file.name);
        return; // Wait for user confirmation via preview
      } else if (type === 'ocr') {
        formData.append('file', file);

        // Create AbortController for timeout
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 60000); // 60 second client-side timeout

        let response;
        try {
          response = await fetch(`/api/analyze/ocr?mode=matrix&category=Driver&club_type=Driver&dexterity=${dexterity}&playing_conditions=false`, {
            method: 'POST',
            body: formData,
            signal: controller.signal,
          });
        } catch (fetchErr) {
          clearTimeout(timeoutId);
          if (fetchErr.name === 'AbortError') {
            throw new Error('OCR processing timed out after 60 seconds. Please try a smaller image or use CSV upload.');
          }
          throw fetchErr;
        }

        clearTimeout(timeoutId);

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          const errorMsg = typeof errorData.detail === 'string'
            ? errorData.detail
            : typeof errorData.detail === 'object'
            ? JSON.stringify(errorData.detail)
            : errorData.message || 'OCR processing failed';
          throw new Error(errorMsg);
        }

        const data = await response.json();

        // Validate response structure
        if (!data || !data.shots || !Array.isArray(data.shots)) {
          throw new Error('Invalid OCR response format');
        }

        // DEBUG: Log what backend returned
        console.log('=== OCR UPLOAD RESPONSE ===');
        console.log('Full response:', data);
        console.log('Has validation:', !!data.validation);
        console.log('Validation array length:', data.validation?.length);
        console.log('First shot:', data.shots?.[0]);
        console.log('===========================');

        // Show extraction preview for user verification
        setExtractedData(data);
        setPendingFileName(file.name);
        // Note: Don't proceed yet - loading will be stopped in finally block
        return; // Wait for user confirmation via preview
      }
    } catch (err) {
      // Better error serialization to handle various error formats
      let errorMessage = 'Upload failed. Please try again.';

      if (err.message) {
        errorMessage = err.message;
      } else if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      } else if (typeof err === 'string') {
        errorMessage = err;
      } else if (err && typeof err === 'object') {
        // Last resort: stringify the error object
        try {
          errorMessage = JSON.stringify(err);
        } catch (stringifyErr) {
          errorMessage = 'Upload failed with an unknown error.';
        }
      }

      console.error('Upload error:', err);
      setError(errorMessage);
    } finally {
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }
      setLoading(false);
    }
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }
    };
  }, []);

  const handlePreviewConfirm = (editedData) => {
    // User confirmed the extraction - proceed with analysis
    const sessionData = transformToSessionData(editedData, pendingFileName);
    onUploadComplete(sessionData);
    // Reset preview state
    setExtractedData(null);
    setPendingFileName(null);
  };

  const handlePreviewCancel = () => {
    // User canceled - reset and allow re-upload
    setExtractedData(null);
    setPendingFileName(null);
    setError(null);
  };

  const loadDemoData = () => {
    // Mock session data matching the spec
    const demoData = {
      session: {
        id: 'session-demo',
        name: 'Driver Fitting Session',
        date: '2026-01-29',
        shotGroups: [
          {
            id: 'group-1',
            name: 'Graphite Design Tour AD F1 5 S Mizuno 1 Up',
            color: '#ff6600',
            isBaseline: true,
            shots: [
              { shotNumber: 1, included: true, metrics: { clubSpeed: 93.2, ballSpeed: 139.4, smashFactor: 1.49, launchAngle: 12.3, spinRate: 1870, spinAxis: 2.3, sideSpin: 150, carry: 217.3, total: 259.7, landingAngle: 27.6, height: 60, attackAngle: 4.5, offlineDistance: 5, clubPath: -1.2, faceAngle: 0.5, dynamicLoft: 13.1 }},
              { shotNumber: 2, included: true, metrics: { clubSpeed: 95.1, ballSpeed: 139.2, smashFactor: 1.46, launchAngle: 12.9, spinRate: 2240, spinAxis: -1.8, sideSpin: -120, carry: 224.3, total: 257.3, landingAngle: 31.8, height: 71, attackAngle: 3.7, offlineDistance: -8, clubPath: -0.8, faceAngle: -0.3, dynamicLoft: 13.5 }},
              { shotNumber: 3, included: true, metrics: { clubSpeed: 94.3, ballSpeed: 140.1, smashFactor: 1.49, launchAngle: 11.8, spinRate: 1920, spinAxis: 1.5, sideSpin: 80, carry: 219.5, total: 261.2, landingAngle: 28.3, height: 62, attackAngle: 4.2, offlineDistance: 3, clubPath: -1.5, faceAngle: 0.2, dynamicLoft: 12.9 }},
              { shotNumber: 4, included: false, metrics: { clubSpeed: 89.7, ballSpeed: 132.5, smashFactor: 1.48, launchAngle: 13.5, spinRate: 2450, spinAxis: -3.2, sideSpin: -210, carry: 205.8, total: 248.1, landingAngle: 32.1, height: 68, attackAngle: 3.1, offlineDistance: -12, clubPath: 0.5, faceAngle: -1.2, dynamicLoft: 14.2 }},
              { shotNumber: 5, included: true, metrics: { clubSpeed: 94.8, ballSpeed: 141.2, smashFactor: 1.49, launchAngle: 12.1, spinRate: 1890, spinAxis: 0.8, sideSpin: 50, carry: 221.7, total: 263.5, landingAngle: 27.9, height: 61, attackAngle: 4.3, offlineDistance: 2, clubPath: -1.3, faceAngle: 0.1, dynamicLoft: 13.0 }},
            ],
          },
          {
            id: 'group-2',
            name: 'Mitsubishi Tensei CK Pro Orange 60 S',
            color: '#3b82f6',
            isBaseline: false,
            shots: [
              { shotNumber: 1, included: true, metrics: { clubSpeed: 93.5, ballSpeed: 140.3, smashFactor: 1.50, launchAngle: 11.5, spinRate: 1780, spinAxis: 1.2, sideSpin: 70, carry: 220.1, total: 262.8, landingAngle: 26.8, height: 58, attackAngle: 4.7, offlineDistance: 4, clubPath: -1.8, faceAngle: 0.3, dynamicLoft: 12.6 }},
              { shotNumber: 2, included: true, metrics: { clubSpeed: 94.2, ballSpeed: 141.8, smashFactor: 1.50, launchAngle: 11.8, spinRate: 1820, spinAxis: -0.9, sideSpin: -60, carry: 223.5, total: 265.3, landingAngle: 27.2, height: 59, attackAngle: 4.5, offlineDistance: -3, clubPath: -1.5, faceAngle: -0.2, dynamicLoft: 12.8 }},
              { shotNumber: 3, included: true, metrics: { clubSpeed: 93.8, ballSpeed: 140.9, smashFactor: 1.50, launchAngle: 11.2, spinRate: 1750, spinAxis: 0.5, sideSpin: 30, carry: 221.8, total: 264.1, landingAngle: 26.5, height: 57, attackAngle: 4.8, offlineDistance: 1, clubPath: -2.0, faceAngle: 0.1, dynamicLoft: 12.5 }},
            ],
          },
        ],
      },
    };
    onUploadComplete(demoData);
  };

  // Show extraction preview if OCR data is available
  if (extractedData) {
    return (
      <div className="max-w-5xl mx-auto">
        <ExtractionPreview
          extractedData={extractedData}
          onConfirm={handlePreviewConfirm}
          onCancel={handlePreviewCancel}
        />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <div className="w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mb-4" />
        <p className="text-text-secondary">Processing your data...</p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-500/10 to-blue-500/5 border border-blue-500/20 rounded-xl p-6 mb-6">
        <div className="flex items-start gap-4">
          <div className="bg-blue-500 rounded-lg p-3 hidden sm:block">
            <BarChart2 className="text-white" size={28} />
          </div>
          <div className="flex-1">
            <h1 className="text-2xl md:text-3xl font-bold text-text-primary mb-2">
              Track Your Sim Session Performance
            </h1>
            <p className="text-text-secondary mb-4">
              Analyze your launch monitor data with TrackMan-style charts. Visualize shot dispersion,
              ball flight trajectories, and club performance to track your improvement over time.
            </p>
            <div className="flex flex-wrap gap-4 text-sm text-text-secondary">
              <div className="flex items-center gap-2">
                <Check className="text-success" size={16} />
                <span>Shot dispersion patterns</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="text-success" size={16} />
                <span>Ball flight visualization</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="text-success" size={16} />
                <span>Session export & sharing</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-error/10 border border-error rounded-lg flex items-start gap-3">
          <AlertCircle className="text-error flex-shrink-0 mt-0.5" size={20} />
          <div>
            <p className="text-sm font-semibold text-error">Upload Error</p>
            <p className="text-sm text-error">{error}</p>
          </div>
        </div>
      )}

      {/* Dexterity Selector */}
      <div className="mb-6 flex items-center justify-center gap-3">
        <span className="text-sm font-medium text-text-secondary">Select your hand:</span>
        <div className="flex gap-2 bg-surface border border-border rounded-lg p-1">
          <button
            onClick={() => setDexterity('RH')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
              dexterity === 'RH'
                ? 'bg-primary text-primary-foreground shadow-sm'
                : 'text-text-secondary hover:text-text-primary hover:bg-surface-light'
            }`}
          >
            🏌️‍♂️ Right-Handed
          </button>
          <button
            onClick={() => setDexterity('LH')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
              dexterity === 'LH'
                ? 'bg-primary text-primary-foreground shadow-sm'
                : 'text-text-secondary hover:text-text-primary hover:bg-surface-light'
            }`}
          >
            🏌️ Left-Handed
          </button>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4 mb-6">
        {/* CSV Upload */}
        <label className="block">
          <input
            type="file"
            accept=".csv"
            onChange={(e) => handleFileUpload(e, 'csv')}
            className="hidden"
          />
          <div className="cursor-pointer border-2 border-gray-300 border-dashed rounded-lg p-6 hover:border-primary hover:bg-primary/10 transition-all group">
            <div className="text-center">
              <FileText className="mx-auto mb-3 text-text-muted group-hover:text-primary transition-colors" size={32} />
              <h3 className="font-semibold text-text-primary mb-1">Upload CSV</h3>
              <p className="text-xs text-text-muted">
                TrackMan, GCQuad, etc.
              </p>
            </div>
          </div>
        </label>

        {/* OCR Upload */}
        <label className="block">
          <input
            type="file"
            accept="image/*"
            onChange={(e) => handleFileUpload(e, 'ocr')}
            className="hidden"
          />
          <div className="cursor-pointer border-2 border-gray-300 border-dashed rounded-lg p-6 hover:border-primary hover:bg-primary/10 transition-all group">
            <div className="text-center">
              <Image className="mx-auto mb-3 text-text-muted group-hover:text-primary transition-colors" size={32} />
              <h3 className="font-semibold text-text-primary mb-1">Take Photo</h3>
              <p className="text-xs text-text-muted">
                OCR extracts data
              </p>
            </div>
          </div>
        </label>

        {/* Demo Data */}
        <button
          onClick={loadDemoData}
          className="border-2 border-gray-300 border-dashed rounded-lg p-6 hover:border-primary hover:bg-primary/10 transition-all group"
        >
          <div className="text-center">
            <Upload className="mx-auto mb-3 text-text-muted group-hover:text-primary transition-colors" size={32} />
            <h3 className="font-semibold text-text-primary mb-1">Try Demo</h3>
            <p className="text-xs text-text-muted">
              See how it works
            </p>
          </div>
        </button>
      </div>

      <div className="text-center text-sm text-text-muted">
        <p className="mb-2">
          <strong>Supported formats:</strong> TrackMan, GCQuad, FlightScope, Foresight, Mevo+, SkyTrak
        </p>
        <p className="text-xs">Upload CSV files or screenshots • Data is not saved - this is a visualization tool only</p>
      </div>
    </div>
  );
}
